import { PRICE_MODE_OPTIONS, RETAILER_OPTIONS } from "./catalogFilters";

const VALID_RETAILERS = new Set(RETAILER_OPTIONS.map((o) => o.id));
const VALID_PRICE_MODES = new Set(PRICE_MODE_OPTIONS.map((o) => o.id));

// In-memory only: survives SPA back/forward, clears on full page refresh.
let homeQuickCompareMemory = null;

export function isOfferModeFromSearchParams(searchParams) {
  if (!searchParams) return false;
  return (
    searchParams.get("offer") === "true" ||
    searchParams.get("price_mode") === "offer_loyalty"
  );
}

export function applyOfferModeToSearchParams(searchParams, isOfferMode) {
  const next = new URLSearchParams(searchParams || "");
  if (isOfferMode) {
    next.set("offer", "true");
    next.set("price_mode", "offer_loyalty");
  } else {
    next.delete("offer");
    next.delete("price_mode");
  }
  return next;
}

function parseCsvSet(raw) {
  if (!raw) return new Set();
  return new Set(
    String(raw)
      .split(",")
      .map((x) => String(x || "").trim())
      .filter(Boolean)
  );
}

/** Values that may contain commas — use repeated query params (?category=A&category=B). */
function parseMultiValueSet(searchParams, key) {
  if (!searchParams) return new Set();
  if (typeof searchParams.getAll === "function") {
    const values = searchParams.getAll(key);
    if (values.length > 0) {
      return new Set(values.map((x) => String(x || "").trim()).filter(Boolean));
    }
    return new Set();
  }
  const raw = searchParams.get?.(key);
  if (!raw) return new Set();
  return new Set([String(raw).trim()].filter(Boolean));
}

function setCsvParam(searchParams, key, values) {
  const next = searchParams instanceof URLSearchParams
    ? searchParams
    : new URLSearchParams(searchParams || "");
  const list = Array.isArray(values)
    ? values
    : values instanceof Set
    ? Array.from(values)
    : [];
  const cleaned = list.map((x) => String(x || "").trim()).filter(Boolean);
  if (!cleaned.length) next.delete(key);
  else next.set(key, cleaned.join(","));
  return next;
}

function setMultiValueParam(searchParams, key, values) {
  const next = searchParams instanceof URLSearchParams
    ? searchParams
    : new URLSearchParams(searchParams || "");
  next.delete(key);
  const list = Array.isArray(values)
    ? values
    : values instanceof Set
    ? Array.from(values)
    : [];
  const cleaned = list.map((x) => String(x || "").trim()).filter(Boolean);
  cleaned.forEach((value) => next.append(key, value));
  return next;
}

/** Stable key for selected filter URL params (supports repeated category/sub_category values). */
export function getCatalogFilterParamsKey(searchParams, { includeBrands = true } = {}) {
  const parts = [];
  if (includeBrands) parts.push(String(searchParams?.get("brands") || ""));
  if (typeof searchParams?.getAll === "function") {
    parts.push(searchParams.getAll("category").join("\u001f"));
    parts.push(searchParams.getAll("sub_category").join("\u001f"));
  } else {
    parts.push(String(searchParams?.get("category") || ""));
    parts.push(String(searchParams?.get("sub_category") || ""));
  }
  return parts.join("|");
}

/** Stable key segment for selected filter values (safe for names containing commas). */
export function joinCatalogFilterValues(values) {
  const list =
    values instanceof Set
      ? Array.from(values)
      : Array.isArray(values)
      ? values
      : [];
  return list
    .map((x) => String(x || "").trim())
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b))
    .join("\u001f");
}

export function hasCatalogListFilters(searchParams, { includeBrands = true } = {}) {
  if (includeBrands && String(searchParams?.get("brands") || "").trim()) return true;
  if (typeof searchParams?.getAll === "function") {
    if (searchParams.getAll("category").length > 0) return true;
    if (searchParams.getAll("sub_category").length > 0) return true;
    return false;
  }
  return Boolean(
    String(searchParams?.get("category") || "").trim() ||
      String(searchParams?.get("sub_category") || "").trim()
  );
}

/** Read Brands / Category / Sub Category (+ optional page) from URL. */
export function readCatalogListFilters(searchParams, { includeBrands = true } = {}) {
  const brands = includeBrands ? parseCsvSet(searchParams?.get("brands")) : new Set();
  const categories = parseMultiValueSet(searchParams, "category");
  const subcategories = parseMultiValueSet(searchParams, "sub_category");
  const pageRaw = Number.parseInt(String(searchParams?.get("page") || "1"), 10);
  const page = Number.isFinite(pageRaw) && pageRaw > 0 ? pageRaw : 1;
  return { brands, categories, subcategories, page };
}

/** Write Brands / Category / Sub Category (+ optional page) into URL params. */
export function applyCatalogListFilters(
  searchParams,
  { brands, categories, subcategories, page } = {}
) {
  const next = new URLSearchParams(searchParams || "");
  if (brands !== undefined) setCsvParam(next, "brands", brands);
  if (categories !== undefined) setMultiValueParam(next, "category", categories);
  if (subcategories !== undefined) setMultiValueParam(next, "sub_category", subcategories);
  if (page !== undefined) {
    if (!page || page <= 1) next.delete("page");
    else next.set("page", String(page));
  }
  return next;
}

export function clearCatalogListFilters(searchParams, { includeBrands = true } = {}) {
  const next = new URLSearchParams(searchParams || "");
  if (includeBrands) next.delete("brands");
  next.delete("category");
  next.delete("sub_category");
  next.delete("page");
  return next;
}

/** Apply a new search query and clear category/subcategory (and optional brand) filters. */
export function applyCatalogSearchQuery(
  searchParams,
  q,
  { includeBrands = true } = {}
) {
  const next = clearCatalogListFilters(searchParams, { includeBrands });
  const trimmed = String(q || "").trim();
  if (trimmed) next.set("q", trimmed);
  else next.delete("q");
  return next;
}

export function getHomeQuickCompareMemory() {
  if (!homeQuickCompareMemory) return null;
  const retailer = VALID_RETAILERS.has(homeQuickCompareMemory.retailer)
    ? homeQuickCompareMemory.retailer
    : "all";
  const priceMode = VALID_PRICE_MODES.has(homeQuickCompareMemory.priceMode)
    ? homeQuickCompareMemory.priceMode
    : "base";
  return { retailer, priceMode };
}

export function setHomeQuickCompareMemory({ retailer, priceMode }) {
  homeQuickCompareMemory = {
    retailer: VALID_RETAILERS.has(retailer) ? retailer : "all",
    priceMode: VALID_PRICE_MODES.has(priceMode) ? priceMode : "base",
  };
}

export function clearHomeQuickCompareMemory() {
  homeQuickCompareMemory = null;
}

export function loadHomeQuickCompareTabs() {
  return getHomeQuickCompareMemory() || { retailer: "all", priceMode: "base" };
}

export function saveHomeQuickCompareTabs({ retailer, priceMode }) {
  setHomeQuickCompareMemory({ retailer, priceMode });
}
